import { Injectable } from '@angular/core'
import ExcelJS from 'exceljs'
import * as FileSaver from 'file-saver'
import { TRACKING_VIEW_COLUMNS } from './tracking.model'

@Injectable({
  providedIn: 'root',
})
export class ExportTrackingDatesService {
  async exportTrackingData(trackingData: any[]): Promise<void> {
    if (!trackingData?.length) {
      console.warn('No tracking data to export')
      return
    }

    /** Step 1: Collect all unique "columnName:value" keys */
    const allKeys = new Set<string>()

    trackingData.forEach((row) => {
      const cols = Array.isArray(row.columns) ? row.columns : []
      const audits = Array.isArray(row.auditColumns) ? row.auditColumns : []

      cols.forEach((c: any) => {
        const key = this.keyFromCol(c)
        if (key) allKeys.add(key)
      })
      audits.forEach((a: any) => {
        const key = this.keyFromAudit(a)
        if (key) allKeys.add(key)
      })
    })

    /** Step 2: Build column order from TRACKING_VIEW_COLUMNS */
    const excluded = ['Account Executive', 'Support', 'Analytics']

    const uiOrderedKeys = TRACKING_VIEW_COLUMNS.filter(
      (col) => !excluded.includes(String(col.label)),
    )
      .flatMap((col) => {
        if (!col.references || col.references.length === 0) {
          return [col.label]
        }
        const children = col.references
          .map((ref) => ref.viewValue?.trim())
          .filter((v) => v && v !== '')
          .filter((v) => v && v.toUpperCase() !== 'N/A')
          .map((v) => `${col.label}: ${v}`)

        return children.length > 0 ? children : [col.label]
      })
      .filter((k) => typeof k === 'string')

    const uiOrderMap = new Map(uiOrderedKeys.map((k, i) => [k.toLowerCase(), i]))
    const normalize = (s: string) => s.toLowerCase().trim()

    const uiAllowed = new Map<string, string>()
    uiOrderedKeys.forEach((key) => uiAllowed.set(normalize(key), key))

    const validKeys = Array.from(allKeys)
      .map((k) => k.trim())
      .filter((k) => uiAllowed.has(normalize(k)))

    const mappedKeys = validKeys.map((k) => uiAllowed.get(normalize(k))!)

    const dynamicColumns = mappedKeys.sort((a, b) => {
      const indexA = uiOrderMap.get(normalize(a)) ?? Number.MAX_SAFE_INTEGER
      const indexB = uiOrderMap.get(normalize(b)) ?? Number.MAX_SAFE_INTEGER
      return indexA - indexB
    })

    /** Step 3: Build export rows */
    const exportRows = trackingData.map((row) => {
      const exportRow: Record<string, any> = {}

      exportRow['Client'] = row.client ?? row.clientName ?? ''
      exportRow['Program'] = row.program ?? row.programName ?? ''
      exportRow['Business Unit'] = row.businessUnit ?? ''
      exportRow['Inception Date'] = this.toShortFormat(row.inceptionDate)

      const cols = Array.isArray(row.columns) ? row.columns : []
      const audits = Array.isArray(row.auditColumns) ? row.auditColumns : []

      const columnMap = new Map<string, any>()
      cols.forEach((c: any) => {
        const key = this.keyFromCol(c)
        if (key) columnMap.set(key, c)
      })

      const auditGroups = new Map<string, any[]>()
      audits.forEach((a: any) => {
        const key = this.keyFromAudit(a)
        if (!key) return
        const group = auditGroups.get(key) ?? []
        group.push(a)
        auditGroups.set(key, group)
      })

      dynamicColumns.forEach((key) => {
        let dateValue = ''
        const colMatch = columnMap.get(key)

        if (colMatch?.value?.toUpperCase?.() === 'N/A') {
          dateValue = ''
        } else if (colMatch && this.isValidDate(colMatch.updatedDate)) {
          dateValue = this.toShortFormat(colMatch.updatedDate)
        } else {
          const auditsForKey = auditGroups.get(key) ?? []
          if (auditsForKey.length > 0) {
            const latest = auditsForKey
              .map((a) => ({
                item: a,
                parsed: Date.parse(a.auditCreatedDate ?? ''),
              }))
              .filter((x) => !isNaN(x.parsed))
              .sort((a, b) => b.parsed - a.parsed)[0]?.item

            if (latest?.value?.toUpperCase?.() === 'N/A') {
              dateValue = ''
            } else if (latest && this.isValidDate(latest.updatedDate)) {
              dateValue = this.toShortFormat(latest.updatedDate)
            }
          }
        }
        exportRow[key] = dateValue
      })

      return exportRow
    })

    /** Step 4: Remove columns that have no dates in any row */
    const allColumnKeys = Object.keys(exportRows[0])
    const dateColumns = allColumnKeys.filter(
      (key) =>
        key !== 'Client' &&
        key !== 'Program' &&
        key !== 'Business Unit' &&
        key !== 'Inception Date',
    )

    const columnsWithDates = dateColumns.filter((key) =>
      exportRows.some((row) => row[key] && row[key].trim() !== ''),
    )

    const finalColumns = [
      'Client',
      'Program',
      'Business Unit',
      'Inception Date',
      ...columnsWithDates,
    ]

    const filteredExportRows = exportRows.map((row) => {
      const filtered: Record<string, any> = {}
      finalColumns.forEach((key) => {
        filtered[key] = row[key]
      })
      return filtered
    })

    /** Step 5: Create ExcelJS workbook */
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('Tracking Export', {
      views: [{ state: 'frozen', ySplit: 1 }],
    })

    const headerKeys = finalColumns

    // Header row
    worksheet.addRow(headerKeys)
    const headerRow = worksheet.getRow(1)
    headerRow.font = { name: 'Segoe UI', size: 10, bold: true }
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' }
    headerRow.eachCell((cell) => {
      cell.border = { bottom: { style: 'thin', color: { argb: 'FF000000' } } }
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF2F2F2' },
      }
    })

    // Body rows
    filteredExportRows.forEach((row) => {
      const values = headerKeys.map((key) => row[key])
      const dataRow = worksheet.addRow(values)
      dataRow.eachCell((cell) => {
        cell.font = { name: 'Segoe UI', size: 10 }
        cell.alignment = { vertical: 'middle', horizontal: 'left' }
      })
    })

    // Column widths
    headerKeys.forEach((key, i) => {
      worksheet.getColumn(i + 1).width = Math.max(15, key.length + 5)
    })

    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: headerKeys.length },
    }

    /** Step 6: Save file */
    const buffer = await workbook.xlsx.writeBuffer()
    const fileName = `TrackingData_${new Date().toISOString().split('T')[0]}.xlsx`

    FileSaver.saveAs(
      new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      fileName,
    )
  }

  private keyFromCol(col: any): string | null {
    if (!col) return null
    const name = (col.columnName ?? '').toString().trim()
    const value = (col.value ?? '').toString().trim()

    if (value.toUpperCase() === 'N/A') return null

    if (!name || !value) return null
    return `${name}: ${value}`
  }

  private keyFromAudit(audit: any): string | null {
    if (!audit) return null
    const name = (audit.columnName ?? '').toString().trim()
    const value = (audit.value ?? '').toString().trim()

    if (value.toUpperCase() === 'N/A') return null

    if (!name || !value) return null
    return `${name}: ${value}`
  }

  private toShortFormat(dateLike: string | Date | null | undefined): string {
    if (!dateLike) return ''
    const d = new Date(dateLike)
    if (isNaN(d.getTime())) return ''
    const day = String(d.getDate()).padStart(2, '0')
    const month = d.toLocaleString('en', { month: 'short' })
    const year = d.getFullYear()
    return `${day}-${month}-${year}`
  }

  private isValidDate(dateLike: any): boolean {
    if (!dateLike) return false
    const d = new Date(dateLike)
    return !isNaN(d.getTime())
  }
}
